import { compact } from 'lodash'
import { sanitizeValue } from 'app/utils'
import type { OptionType } from 'types/OptionType'

/**
 * Sentinel location id used to force a zero-result ("match none") contacts query when a
 * location filter is active but resolves to no real locations. It is a constant we own —
 * never user input — and cannot collide with a real location id (which are ObjectIds).
 */
export const MATCH_NONE_LOCATION_ID = `__no_location__`

/**
 * Resolved state of the pipeline board's location filter.
 * - `inactive`  no location filter in the URL → show every location.
 * - `pending`   a filter is active but locations haven't loaded yet → don't query.
 * - `ids`       resolved to one or more real location ids → filter to them.
 * - `none`      active but resolves to zero locations → match nothing (fail closed).
 */
export type ContactLocationFilter =
  | { state: `inactive` }
  | { state: `pending` }
  | { state: `ids`; ids: string[] }
  | { state: `none` }

export type LocationFilterInput = {
  /** `locationIds` URL param, already split/compacted. */
  locationIds: string[]
  /** All locations (from `useLocationIntegrationStatus`) — used to validate the ids. */
  locationOptions: OptionType[]
  /** Whether `locationOptions` is still loading. */
  isLoading: boolean
}

/**
 * Resolve the location selector's URL state to a concrete filter for the contacts query.
 *
 * `locationIds` are validated against the known locations, so a crafted URL cannot smuggle
 * query syntax through (anything that isn't a real location id is dropped). An active filter
 * that resolves to nothing yields `none` (match nothing), not an empty clause — an empty
 * clause would "fail open" and show every location's contacts.
 */
export function resolveContactLocationFilter({
  locationIds,
  locationOptions,
  isLoading,
}: LocationFilterInput): ContactLocationFilter {
  if (locationIds.length === 0) return { state: `inactive` }
  // A filter is active but we can't validate it until locations load.
  if (isLoading) return { state: `pending` }

  const knownIds = new Set(compact(locationOptions.map((o) => o.value)))
  const resolved = locationIds.filter((id) => knownIds.has(id))
  return resolved.length > 0 ? { state: `ids`, ids: resolved } : { state: `none` }
}

/** Quote + strip Lucene specials from a query value using the app's shared sanitizer. */
const esValue = (value: string): string => `"${sanitizeValue(value)}"`

/**
 * ES term for a resolved location filter, for the caller to AND into the contacts query.
 * `inactive` / `pending` contribute nothing (the caller must not query while pending).
 */
export function contactLocationClause(filter: ContactLocationFilter): string {
  switch (filter.state) {
    case `ids`:
      return `locationId:(${filter.ids.map(esValue).join(` OR `)})`
    case `none`:
      return `locationId:${esValue(MATCH_NONE_LOCATION_ID)}`
    case `inactive`:
    case `pending`:
      return ``
  }
}
